import {type BlobOffset, type BlobRange, DOMRangeFromBlob} from '@github-ui/blob-anchor'
import type {CodeSection, CodeSectionLookup} from '@github-ui/code-nav'
import {ObservableSet} from '@github-ui/observable'
import {useObservedState} from '@github-ui/react-observable'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {useSyncExternalStore} from 'react'

import type {CodeLineData} from '../components/blob/BlobContent/Code/hooks/use-code-lines'
import {matchElementId} from '../components/blob/BlobContent/HighlightedOverlay'
import {charWidthMac, useIsCursorEnabled} from '../hooks/use-cursor-navigation'
import type {SetStickyLinesType} from '../hooks/use-sticky-lines'
import {measureMonospaceTextWidth, resetMeasureContext} from './measure-text'

const FIREFOX_VERSION_REGEX = /firefox\/(\d+(\.\d+)?)/
const CHROMIUM_VERSION_REGEX = /Chrom(e|ium)\/([0-9]+)\./
const SYMBOL_MATCH_REGEX_ENT = /\n|\s|[();&.=,]/
const SYMBOL_MATCH_REGEX_DEFAULT = /\n|\s|[();&.=",]/

/**
 * Resets the cached canvas measurement context and font properties.
 * Delegates to the shared measure-text utility. Re-exported for backward compatibility.
 */
export const resetMeasurementCache = resetMeasureContext

const rowCollapseStyleId = 'collapse-show-rows-styles'
const CODE_CELL_PADDING = 10
export const textAreaId = 'read-only-cursor-text-area'

export function queryLineElement(anchorPrefix: string, line: number) {
  return document.querySelector<HTMLElement>(`#${anchorPrefix}LC${line}`)
}

export function queryMatchElement(line: number, column: number) {
  return document.querySelector<HTMLElement>(`main #${matchElementId(line, column)}`)
}

const collapsedRows = new ObservableSet<number>()

export function useIsLineCollapsed(lineNumber: number) {
  return useObservedState(collapsedRows.has(lineNumber))
}
// Non-reactive read of the authoritative collapsed-rows set, keyed by a section's start line.
// Use this in event handlers, observer callbacks, and pure computations where a hook can't run.
export function isRowCollapsed(lineNumber: number): boolean {
  return collapsedRows.value.has(lineNumber)
}
// Stable, immutable snapshot of `collapsedRows`, rebuilt only when the set actually changes
// (tracked via the observable's monotonic `version`). `collapsedRows` mutates its backing Set in
// place and notifies with the *same* reference, so subscribers that read `.value` directly get an
// identity that never changes. Returning a fresh Set reference on each change is what lets every
// memoization layer — manual `useMemo` dependency arrays *and* the React Compiler's automatic
// memoization — detect the change. Without it, collapsed sections are hidden via CSS but never
// shrink the virtualizer, because the shrink memo stays keyed on a Set whose identity is constant.
// Server render never has collapsed rows (collapse is a client-only interaction), so a single
// shared empty set is a safe, stable server snapshot.
const EMPTY_COLLAPSED_ROWS: ReadonlySet<number> = new Set()

let collapsedRowsSnapshot: ReadonlySet<number> = EMPTY_COLLAPSED_ROWS
let collapsedRowsSnapshotVersion = -1

function getCollapsedRowsSnapshot(): ReadonlySet<number> {
  if (collapsedRows.version !== collapsedRowsSnapshotVersion) {
    collapsedRowsSnapshotVersion = collapsedRows.version
    collapsedRowsSnapshot = collapsedRows.value.size === 0 ? EMPTY_COLLAPSED_ROWS : new Set(collapsedRows.value)
  }
  return collapsedRowsSnapshot
}

// Module-scope `useSyncExternalStore` arguments. `collapsedRows` is a singleton, so none of these
// depend on render scope. Hoisting `subscribe` is the load-bearing part: an inline arrow is a new
// reference each render, which makes React unsubscribe + re-subscribe on every commit of every
// consumer. The server snapshot returns the shared empty set so SSR/hydration stays stable too.
function subscribeToCollapsedRows(onStoreChange: () => void): () => void {
  return collapsedRows.subscribe(onStoreChange)
}

function getCollapsedRowsServerSnapshot(): ReadonlySet<number> {
  return EMPTY_COLLAPSED_ROWS
}

export function useCollapsedLinesSubscription(): ReadonlySet<number> {
  return useSyncExternalStore(subscribeToCollapsedRows, getCollapsedRowsSnapshot, getCollapsedRowsServerSnapshot)
}

export function useShouldUseInert() {
  const isFirefox = useIsFirefox()
  const firefoxVersion = useFirefoxVersion() ?? 0
  const isChromiumOrEdge = useIsChromeOrEdge()
  const chromiumVersion = useChromiumVersion() ?? 0

  // 124 is the first version that supports inert hiding text
  const isSupportedVersion = firefoxVersion >= 124 || chromiumVersion >= 124

  // we only current use inert on the textarea overlay
  return useIsCursorEnabled() && (isFirefox || isChromiumOrEdge) && isSupportedVersion
}

export function useIsFirefox(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => navigator.userAgent.toLowerCase().indexOf('firefox') > -1,
    () => false,
  )
}

function useIsChromeOrEdge(): boolean {
  return useSyncExternalStore(
    () => () => {},

    () =>
      navigator.userAgent.toLowerCase().indexOf('chrome') > -1 ||
      navigator.userAgent.toLowerCase().indexOf('chromium') > -1 ||
      navigator.userAgent.toLowerCase().indexOf('edge') > -1,
    () => false,
  )
}

/**
 * Extracts the Firefox major version number from the user agent string
 * @param userAgent - User agent string
 * @returns Firefox major version number or null if not found
 */
function getFirefoxVersion(userAgent: string) {
  // example - Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:97.0) Gecko/20100101 Firefox/97.0"; => 97
  // example - Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36' => null
  const match = userAgent.toLowerCase().match(FIREFOX_VERSION_REGEX)
  return match && match.length >= 2 && match[1] ? parseFloat(match[1]) : null
}

/**
 * Extracts the edge or chrome major version number from the user agent string
 * @param userAgent - User agent string
 * @returns edge or chrome major version number or null if not found
 */
function getChromiumVersion(userAgent: string) {
  // example - Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:97.0) Gecko/20100101 Firefox/97.0"; => null
  // example - Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36' => 121
  const match = userAgent.match(CHROMIUM_VERSION_REGEX)

  return match && match.length >= 3 && match[2] ? parseFloat(match[2]) : null
}

function useChromiumVersion(): number | null {
  return useSyncExternalStore(
    () => () => {},
    () => getChromiumVersion(navigator.userAgent),
    () => null,
  )
}

function useFirefoxVersion(): number | null {
  return useSyncExternalStore(
    () => () => {},
    () => getFirefoxVersion(navigator.userAgent),
    () => null,
  )
}

export function buildRowClass(
  codeSectionArray: CodeSection[],
  lineNumber: number,
  isEndLine: boolean,
  ownedCodeSections: Map<number, CodeSection[]> | null,
) {
  if (!ownedCodeSections) return ''

  let rowHideClassName = ''
  for (let i = 0; i < codeSectionArray.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    rowHideClassName += `${getLineChildClassName(codeSectionArray[i]!.startLine - 1)} `
  }
  if (isEndLine && ownedCodeSections.has(lineNumber)) {
    const sections = ownedCodeSections.get(lineNumber)
    if (sections) {
      for (let j = 0; j < sections.length; j++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        rowHideClassName += `${getLineChildClassName(sections[j]!.startLine - 1)} `
      }
    }
  }
  return rowHideClassName
}

function getLineChildClassName(lineNumber: number) {
  return `child-of-line-${lineNumber}`
}

export function applyStickyToParentStartLines(
  map: Map<number, CodeLineData[]>,
  line: number,
  codeLineToSectionMap: CodeSectionLookup | undefined,
  onLineStickOrUnstick: SetStickyLinesType,
) {
  if (!codeLineToSectionMap) return
  const sections = codeLineToSectionMap.get(line)
  if (sections) {
    for (const section of sections) {
      const startLines = map.get(section.endLine)
      if (startLines) {
        for (const startLine of startLines) {
          if (line > startLine.lineNumber) {
            onLineStickOrUnstick(startLine, false)
          }
        }
      }
    }
  }
}

export function expandRow(lineNumber: number) {
  collapsedRows.delete(lineNumber)

  if (syncCollapseSheetFromSet()) {
    return
  }

  setStylesForRow(lineNumber, false)
}
export function collapseRow(lineNumber: number) {
  collapsedRows.add(lineNumber)

  if (syncCollapseSheetFromSet()) {
    return
  }

  setStylesForRow(lineNumber, true)
}

export function expandAllRows() {
  collapsedRows.clear()

  if (syncCollapseSheetFromSet()) {
    return
  }

  const styleSheet = document.getElementById(rowCollapseStyleId)
  if (styleSheet) {
    styleSheet.textContent = ''
  }
}

// Collapse/expand is an INP-sensitive interaction that can hide an unbounded number of
// descendant rows (including rows the virtualizer has not mounted yet). To keep the hide off the
// React render path we drive it through a single stylesheet rather than component state.
//
// `collapsedRows` is the authoritative source of truth. We keep one constructable CSSStyleSheet
// adopted on the document and rebuild it from that set via replaceSync — so we never scan the
// DOM/CSSOM to find rules to add or remove, and we never grow an ever-larger textContent string
// that the browser must reparse from scratch on every toggle.
let collapseSheet: CSSStyleSheet | null = null

// Not a browser-support guard — constructable stylesheets are available in every browser we
// support (see .browserslistrc). This probe exists purely for SSR/jsdom safety: it returns false
// when there is no `document` (server render) or when the test DOM lacks adoptedStyleSheets /
// replaceSync, letting callers fall back instead of throwing.
function supportsConstructableStyleSheets(): boolean {
  return (
    typeof document !== 'undefined' &&
    'adoptedStyleSheets' in Document.prototype &&
    typeof CSSStyleSheet === 'function' &&
    typeof CSSStyleSheet.prototype.replaceSync === 'function'
  )
}

function getCollapseStyleSheet(): CSSStyleSheet | null {
  if (!supportsConstructableStyleSheets()) return null
  if (!collapseSheet) {
    collapseSheet = new CSSStyleSheet()
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, collapseSheet]
  }
  return collapseSheet
}

// Rebuild the collapse stylesheet from `collapsedRows`. Returns false only in SSR/jsdom
// environments without constructable stylesheet support so callers can fall back to the legacy
// textContent path.
function syncCollapseSheetFromSet(): boolean {
  const sheet = getCollapseStyleSheet()
  if (!sheet) return false

  let css = ''
  for (const lineNumber of collapsedRows.value) {
    css += `.${getLineChildClassName(lineNumber - 1)} { display: none; } `
  }
  sheet.replaceSync(css)
  return true
}

// Legacy fallback used only when constructable stylesheets are unavailable. Toggles rows by
// mutating a <style> element's textContent, which grows the sheet string over the session and
// forces a full reparse on every toggle.
function setStylesForRow(lineNumber: number, isCollapsed: boolean) {
  const styles = `.${getLineChildClassName(lineNumber - 1)} { display: none; } `

  const styleSheet = document.getElementById(rowCollapseStyleId)
  if (styleSheet) {
    if (isCollapsed) {
      styleSheet.textContent += styles
    } else {
      styleSheet.textContent = (styleSheet.textContent || '').replace(styles, '')
    }
  } else {
    //this case will ideally only run once, the very first time someone collapses a row
    const newStyleSheet = document.createElement('style')
    newStyleSheet.id = rowCollapseStyleId
    newStyleSheet.textContent = styles
    document.head.appendChild(newStyleSheet)
  }
}

const codeTextAttribute = 'data-code-text'

export function isNodeTokenNode(line: SafeHTMLString, charOffset: number): boolean {
  const fragment = document.createRange().createContextualFragment(line)
  let textLengthSoFar = 0
  for (const node of [...fragment.childNodes]) {
    if (node instanceof HTMLElement) {
      if (node.textContent) {
        textLengthSoFar += node.textContent.length
      } else if (node.getAttribute(codeTextAttribute)) {
        textLengthSoFar += (node.getAttribute(codeTextAttribute) || '').length
      }
      // >= because we want the end of a symbol to count as in the symbol node
      if (textLengthSoFar >= charOffset) {
        if (isSymbolNode(node)) {
          return true
        } else {
          return false
        }
      }
    } else if (node instanceof Text) {
      if (node.textContent) {
        textLengthSoFar += node.textContent.length
      }
      //only greater than not equal to because we want a the beginning of a symbol to count as in the symbol node
      if (textLengthSoFar > charOffset) {
        return false
      }
    }
  }
  return false
}

export function isSymbolNode(node: Node): node is HTMLSpanElement {
  if (!(node instanceof HTMLSpanElement)) return false

  const text = node.getAttribute(codeTextAttribute) ?? node.textContent ?? ''
  const classes = [...node.classList].join(' ')

  return isSymbol(text, classes)
}

export function isSymbol(text: string, className: string) {
  if (text.length < 3) return false

  const classesArray = className.split(' ')
  //we are okay with quotes being present in the symbol if the symbol's class is pl-ent
  const matchRegEx = classesArray.includes('pl-ent') ? SYMBOL_MATCH_REGEX_ENT : SYMBOL_MATCH_REGEX_DEFAULT
  if (text.match(matchRegEx)) return false

  if (classesArray.includes('pl-c') || classesArray.includes('pl-k')) return false // comments or keywords

  return true
}

export function getCodeSymbolFromPoint(x: number, y: number): string | null {
  let nodeAtPoint: Node | undefined = undefined

  nodeAtPoint ??= document.caretPositionFromPoint?.(x, y)?.offsetNode
  if (nodeAtPoint) {
    //for firefox we need to do additional massaging to get the actual element that has the clicked text
    const offset = document.caretPositionFromPoint?.(x, y)?.offset ?? 1
    //item list is 0 indexed, offset is 1 indexed
    nodeAtPoint = nodeAtPoint.childNodes.item(offset - 1)
  }
  // eslint-disable-next-line compat/compat
  nodeAtPoint ??= document.caretRangeFromPoint?.(x, y)?.startContainer

  if (!nodeAtPoint || !isSymbolNode(nodeAtPoint)) return null

  return nodeAtPoint.getAttribute(codeTextAttribute)
}

/**
 * From a text node and offset, find the line number that line is on and the offset within that line
 * @param textNode The node to find the line number for
 * @param offset The offset within the text node
 * @returns The line number and character column of the line
 */
export function calculateLineAndColumn(textNode: Node, offset: number): BlobOffset | undefined {
  let lineNumber = null
  let lineElement = null
  let childNode = null
  let column = offset

  // Determine if the code is highlighted or not
  if (textNode.parentElement?.classList.contains('react-file-line')) {
    lineNumber = textNode.parentElement.getAttribute('data-line-number')
    lineElement = textNode.parentElement
    childNode = textNode
  } else if (textNode.parentElement?.parentElement?.classList.contains('react-file-line')) {
    lineNumber = textNode.parentElement.parentElement.getAttribute('data-line-number')
    lineElement = textNode.parentElement.parentElement
    childNode = textNode.parentNode
    // If we highlight to the end of the line, the textNode will be the line element
  } else if (textNode.parentElement?.firstElementChild?.classList.contains('react-file-line')) {
    lineNumber = textNode.parentElement.firstElementChild.getAttribute('data-line-number')
    if (!lineNumber || !parseInt(lineNumber, 10)) {
      return
    }
    return {line: parseInt(lineNumber, 10) - 1, column: null}
  } else {
    return
  }

  if (!lineNumber || !parseInt(lineNumber, 10)) {
    return
  }

  for (const child of lineElement.childNodes) {
    if (child === childNode) {
      break
    }
    column += child.textContent?.length || 0
  }

  return {line: parseInt(lineNumber, 10), column: column !== 0 ? column + 1 : null}
}

export interface HighlightPosition {
  offset?: number
  width?: number
}

/**
 * Measures text width using Canvas API instead of DOM manipulation.
 * This avoids layout thrashing by not creating/appending/measuring/removing DOM elements.
 * Returns an integer to match DOM's clientWidth behavior.
 */
function measureTextWithCanvas(charOffset: number, lineRawText: string, tabSize: number): number {
  const textToMeasure = lineRawText.slice(0, charOffset)

  const width = measureMonospaceTextWidth(textToMeasure, tabSize)
  if (width > 0 || charOffset === 0) {
    // Round to match DOM's clientWidth which returns an integer
    return Math.round(width)
  }

  // Fallback: Canvas unavailable, return approximation
  return charOffset !== 0 ? charOffset * charWidthMac : 0
}

/**
 * Determines the pixel offset for a character position in a line of code.
 * Uses an offscreen Canvas to measure the rendered width of the substring,
 * falling back to a character-width approximation when Canvas is unavailable.
 */
export function determineProperOffsetWithDummyDiv(charOffset: number, lineRawText: string, tabSize: number): number {
  return measureTextWithCanvas(charOffset, lineRawText, tabSize)
}
export function calculateHighlightOffsetAndWidth(
  highlightedInfo: BlobRange,
  lineElement: HTMLElement,
  lineNumber: number,
  tabSize: number,
  rawText: string,
): HighlightPosition | undefined {
  if (highlightedInfo?.start.line === lineNumber && highlightedInfo?.start.column !== null) {
    const range = DOMRangeFromBlob(
      {
        start: highlightedInfo.start,
        end: {
          line: highlightedInfo.start.line,
          column: highlightedInfo.end.line === lineNumber ? highlightedInfo.end.column : null,
        },
      },
      () => lineElement,
    )

    if (range && range.startContainer.parentElement) {
      // Cache getBoundingClientRect() calls to avoid multiple layout calculations
      const rangeRect = range.getBoundingClientRect()
      const lineRect = lineElement.getBoundingClientRect()
      const lineOffset = rangeRect.x - lineRect.x

      return {
        offset: lineOffset + CODE_CELL_PADDING,
        width: highlightedInfo.end.line === lineNumber ? rangeRect.width : undefined,
      }
    } else if (rawText) {
      const offset = determineProperOffsetWithDummyDiv(highlightedInfo.start.column - 1, rawText, tabSize)
      const endColumn = highlightedInfo.end.line === lineNumber ? highlightedInfo.end.column : null

      return {
        // need to multiply by 2 because we're purely calculating the width of the text, not including any padding
        offset: offset + CODE_CELL_PADDING * 2,
        width:
          highlightedInfo.end.line === lineNumber
            ? determineProperOffsetWithDummyDiv(endColumn ? endColumn - 1 : rawText.length - 1, rawText, tabSize) -
              offset
            : undefined,
      }
    }
  } else if (highlightedInfo?.end.line === lineNumber && highlightedInfo?.end.column !== null) {
    const range = DOMRangeFromBlob(
      {
        start: {line: highlightedInfo.end.line, column: 0},
        end: highlightedInfo.end,
      },
      () => lineElement,
    )

    if (range) {
      // Cache getBoundingClientRect() call
      const rangeRect = range.getBoundingClientRect()
      return {
        width: rangeRect.width + CODE_CELL_PADDING,
      }
    } else {
      return {
        width: determineProperOffsetWithDummyDiv(highlightedInfo.end.column - 1, rawText, tabSize) + CODE_CELL_PADDING,
      }
    }
  }
}

export function isLineInViewport(lineNumber: number): boolean {
  const line = queryLineElement('', lineNumber)
  return isInViewport(line)
}
export function getElementForLine(lineNumber: number): HTMLElement | null {
  return queryLineElement('', lineNumber)
}
function isInViewport(element: HTMLElement | null): boolean {
  if (!element) return false

  const rect = element.getBoundingClientRect()
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
}

export function calculateLineNumberFromOffset(pageYOffset: number, topPageSpace: number, lineHeight: number) {
  const line = Math.floor((pageYOffset - topPageSpace) / lineHeight)
  return line + 1 //line numbers are 1 indexed
}

export function getActualLineNumberBinarySearch(wrongLineNumber: number, linesData: readonly CodeLineData[]) {
  let low = 0
  let high = linesData.length - 1
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const midElement = linesData[mid]
    if (!midElement) return -1
    if (midElement.lineNumber === wrongLineNumber) {
      return mid
    } else if (midElement.lineNumber < wrongLineNumber) {
      low = mid + 1
    } else {
      high = mid - 1
    }
  }
  return -1
}

function createScreenReaderAnnouncementDiv() {
  if (typeof ssrSafeDocument === 'undefined') return
  const dummyDiv = ssrSafeDocument.createElement('div')
  dummyDiv.classList.add('sr-only', 'mt-n1')
  dummyDiv.id = 'screenReaderAnnouncementDiv'
  dummyDiv.setAttribute('role', 'alert')
  dummyDiv.setAttribute('data-testid', 'screenReaderAnnouncement')
  dummyDiv.setAttribute('aria-live', 'assertive')
  ssrSafeDocument.body.appendChild(dummyDiv)
}

export function forceAnnouncementToScreenReaders(textToAnnounce: string, timeoutBeforeAnnouncing = 0) {
  if (typeof ssrSafeDocument === 'undefined') return
  let screenReaderDiv = ssrSafeDocument.getElementById('screenReaderAnnouncementDiv')

  if (!screenReaderDiv) {
    createScreenReaderAnnouncementDiv()
  }
  screenReaderDiv = ssrSafeDocument.getElementById('screenReaderAnnouncementDiv')
  if (!screenReaderDiv) return //something went wrong creating the div

  const textToAnnounceDeDuped =
    screenReaderDiv.textContent === textToAnnounce ? `${textToAnnounce}\u00A0` : textToAnnounce

  setTimeout(() => {
    if (screenReaderDiv) {
      screenReaderDiv.textContent = textToAnnounceDeDuped
    }
  }, timeoutBeforeAnnouncing)
}
